import { useEffect, useRef } from 'react';
import { useRequestData } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';
import { LocationsData } from '../data/LocationsData';
import { requestLocations } from '../utilities/locationFetch';
import { useAccountId } from '../features/accountId/accountIdSlice';
import { useDealerPhotoBaseUrl } from '../features/dealerPhoto/dealerPhotoSlice';
import { getDDCLocationsData } from '../utilities/ddcHelper';
import { getDefaultPostalCode } from '../utilities/defaultPostalCode';
import { getInitialRange } from '../utilities/defaultRange';

const LOG_PREFIX = '[ws-locations-data][Widget]';
const LOCATIONS_DATA_READY_EVENT = 'DDC:LocationsDataReady';

const Widget = () => {
	const accountId = useAccountId();
	const { locale } = useRequestData();
	const isFirstLoadRef = useRef(true);
	const dealerPhotoBaseUrl = useDealerPhotoBaseUrl();

	useEffect(() => {
		if (!getDDCLocationsData()) {
			// Create global namespace
			window.DDC = window.DDC || {};

			// Create LocationsData instance with request handler
			window.DDC.LocationsData = new LocationsData(
				(params) => {
					const {
						postalCode,
						range,
						excludeByRange,
						useDefaultTileCount
					} = params;
					// Only use defaultTileCount on first load
					const isDefaultTileCountNeeded =
						useDefaultTileCount && isFirstLoadRef.current;
					isFirstLoadRef.current = false;

					logger.debug(`${LOG_PREFIX} Triggering location request:`, {
						postalCode,
						range,
						excludeByRange,
						accountId,
						locale,
						isDefaultTileCountNeeded
					});

					return requestLocations({
						postalCode,
						range,
						excludeByRange,
						accountId,
						locale,
						isDefaultTileCountNeeded
					});
				},
				{ dealerPhotoBaseUrl }
			);

			/**
			 * Trigger the initial fetch immediately on mount so the request
			 * is in flight before consumer widgets (ws-locations-control,
			 * ws-locations-search, etc) finish mounting. We restore the user's
			 * previous search where possible:
			 *  - postalCode is resolved by getDefaultPostalCode, which
			 *    walks the priority chain: URL geoZip -> DDC.postalCode
			 *    cookie -> userLocation -> prefs default
			 *  - range comes from sessionStorage (persisted across refresh
			 *    by LocationsData.setSearchParams)
			 *
			 * excludeByRange is passed false so the response keeps stores
			 * outside the radius. This is the only caller that passes false —
			 * setSearchParams defaults every consumer widget to true — and
			 * locationFetch relies on that to decide when to seed the
			 * range-agnostic nearestLocations list, since it cannot build that
			 * list from a response the server has already range-filtered.
			 * Keep the false here in sync with the gate in requestLocations.
			 *
			 * Consumer widgets' identical follow-up calls are
			 * deduped by setSearchParams' hasChanged check, so this does
			 * not cause a duplicate fetch.
			 */
			const initialPostalCode = getDefaultPostalCode();
			const initialRange = getInitialRange();

			if (initialPostalCode) {
				logger.debug(
					`${LOG_PREFIX} Triggering initial location fetch`,
					{
						initialPostalCode,
						initialRange
					}
				);

				window.DDC.LocationsData.setSearchParams(
					initialPostalCode,
					initialRange,
					false, // excludeByRange — see above
					true // isDefaultView
				);
			} else {
				logger.debug(
					`${LOG_PREFIX} Skipping initial fetch - missing postal code or stored range`,
					{
						initialPostalCode,
						initialRange
					}
				);
			}

			/**
			 * Dispatch ready event for consuming widgets.
			 * Consumers (ws-locations-search, ws-locations-control) listen for this event
			 * to know when LocationsData is available.
			 * @event DDC:LocationsDataReady
			 */
			logger.debug(`${LOG_PREFIX} Dispatching LocationsDataReady event`);
			window.dispatchEvent(new CustomEvent(LOCATIONS_DATA_READY_EVENT));
		}
	}, [accountId, locale]);

	return null;
};

export default Widget;
